import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, Zap, Crown } from 'lucide-react';

const plans = [
  {
    name: "Free",
    price: 0,
    period: "forever",
    description: "Perfect for getting started with career development",
    features: [
      "Basic career assessment",
      "AI Plan (1/mo, short-term 2–4 weeks, basic)",
      "Limited exercises",
      "Community access",
      "Basic progress tracking"
    ],
    limitations: [
      "No exports & KPIs",
      "Basic salary insights", 
      "No priority support"
    ],
    cta: "Start Free / Sign In",
    ctaLink: "/authentication",
    popular: false,
    icon: Star
  },
  {
    name: "Premium",
    price: 19.99,
    period: "month",
    description: "Unlock the full power of AI-driven career growth",
    features: [
      "Unlimited AI career plans",
      "Advanced skill exercises",
      "Detailed salary insights by ZIP",
      "Resume builder & optimization",
      "1-on-1 coaching sessions", 
      "Priority support",
      "Advanced analytics",
      "Custom goal setting",
      "Industry networking"
    ],
    cta: "Start Premium",
    ctaLink: "/subscription-management",
    popular: true,
    icon: Crown,
    badge: "Most Popular"
  }
];

export default function Pricing() {
  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4" />
            Simple Pricing
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Choose your career growth plan
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free and upgrade when you're ready to accelerate your career progress.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans?.map((plan, index) => {
            const IconComponent = plan?.icon;
            return (
              <div key={index} className={`relative rounded-2xl p-8 ${
                plan?.popular 
                  ? 'bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary shadow-xl' 
                  : 'bg-card border border-border hover:border-primary/20'
              } transition-all duration-300 hover:shadow-lg`}>
                
                {plan?.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                      {plan?.badge}
                    </div>
                  </div>
                )}

                <div className="text-center mb-2">
                  <div className="inline-flex items-center gap-2 mb-4">
                    {IconComponent && <IconComponent className={`w-6 h-6 ${plan?.popular ? 'text-primary' : 'text-muted-foreground'}`} />}
                    <h3 className="text-2xl font-bold text-foreground">{plan?.name}</h3>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-4xl md:text-5xl font-bold text-foreground">
                      ${plan?.price}
                    </span>
                    <span className="text-muted-foreground">/{plan?.period}</span>
                  </div>
                  
                  <p className="text-muted-foreground text-sm">
                    {plan?.description}
                  </p>
                </div>

                <div className="space-y-4 mb-2">
                  {plan?.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500 shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                  
                  {plan?.limitations?.map((limitation, limitationIndex) => (
                    <div key={limitationIndex} className="flex items-center gap-3 opacity-60">
                      <div className="w-5 h-5 rounded-full border-2 border-muted-foreground shrink-0"></div>
                      <span className="text-muted-foreground text-sm line-through">{limitation}</span>
                    </div>
                  ))}
                </div>

                <Link 
                  to={plan?.ctaLink || '/authentication'}
                  className={`block w-full text-center py-1 px-6 rounded-xl font-semibold transition-all duration-200 ${
                    plan?.popular 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5' 
                      : 'bg-card text-primary border-2 border-primary hover:bg-primary/5'
                  }`}
                >
                  {plan?.cta}
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-secondary/10 px-6 py-1 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-green-500" />
              <span>30-day money-back guarantee</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-green-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-green-500" />
              <span>No setup fees</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}